// ─── Roamer Intelligence Engine™ ─────────────────────────────────────────────
// Sprint 113: 21-stage modular intelligence pipeline.
//
// Automatically enriches every KnowledgeItem after the capture pipeline
// completes. All entity extraction is deterministic (pattern-based) —
// zero additional API calls, runs entirely in-browser.
//
// Stages (in order):
//  1 Capture           — validate item has content
//  2 Identify Type     — verify detected content type
//  3 Extract Metadata  — verify metadata is present
//  4 Extract Text      — prepare analysis corpus
//  5 OCR               — skip if not an image
//  6 Transcript        — skip if not audio/video
//  7 AI Summary        — already done by capture pipeline (verify)
//  8 Extract Topics    — keyword → topic category matching
//  9 Extract Keywords  — TF-IDF-style frequency extraction
// 10 Recognize Companies — curated list + corporate-name patterns
// 11 Recognize People  — capitalized two-word name regex
// 12 Recognize Places  — curated nomad/country list
// 13 Recognize Skills  — curated skill dictionary
// 14 Career Types      — curated career-type dictionary
// 15 Suggest Collections — already done by capture pipeline (verify)
// 16 Related Content   — topics-based suggestions (future)
// 17 Search Index      — augment searchIndex with extracted terms
// 18 Knowledge Graph   — update graph (stub — calls VaultKnowledgeGraph if present)
// 19 Store Original    — verify storage location
// 20 Store Intelligence — write enriched item to IndexedDB
// 21 Complete          — emit events, finalize

import type { IntelligenceJob, KnowledgeItem } from '@/types/knowledgeItem';
import { INTELLIGENCE_EVENTS, INTELLIGENCE_STAGES } from '@/types/knowledgeItem';
import { updateKnowledgeItem } from '@/services/KnowledgeStore';

// ─── Job queue state ──────────────────────────────────────────────────────────

const MAX_INTELLIGENCE_JOBS_KEPT = 20;
const MAX_CONCURRENT_INTELLIGENCE = 1; // single-threaded (CPU-intensive)

let intelligenceJobs: IntelligenceJob[] = [];
let activeIntelligenceCount = 0;
const intelligenceQueue: Array<{ job: IntelligenceJob; item: KnowledgeItem }> = [];

function emitIntelligenceJobs(): void {
  window.dispatchEvent(
    new CustomEvent(INTELLIGENCE_EVENTS.jobs, { detail: { jobs: getIntelligenceJobsSnapshot() } }),
  );
}

export function getIntelligenceJobsSnapshot(): IntelligenceJob[] {
  return intelligenceJobs.map(j => ({ ...j }));
}

export function dismissIntelligenceJob(id: string): void {
  intelligenceJobs = intelligenceJobs.filter(j => j.id !== id);
  emitIntelligenceJobs();
}

export function getActiveIntelligenceCount(): number {
  return intelligenceJobs.filter(j => j.status === 'running').length;
}

function makeJobId(): string {
  return `rie-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function advanceJob(job: IntelligenceJob, stageIndex: number, detail?: string): void {
  job.stageIndex = stageIndex;
  job.stage = INTELLIGENCE_STAGES[stageIndex]?.id ?? 'rie-complete';
  job.pct = Math.round((stageIndex / INTELLIGENCE_STAGES.length) * 100);
  job.detail = detail ?? INTELLIGENCE_STAGES[stageIndex]?.label ?? '';
  emitIntelligenceJobs();
}

function finishIntelligenceJob(job: IntelligenceJob): void {
  job.status = 'done';
  job.pct = 100;
  job.stage = 'rie-complete';
  job.stageIndex = INTELLIGENCE_STAGES.length - 1;
  job.detail = 'Intelligence complete';
  job.finishedAt = new Date().toISOString();
  emitIntelligenceJobs();
}

function failIntelligenceJob(job: IntelligenceJob, error: unknown): void {
  job.status = 'error';
  job.error = error instanceof Error ? error.message : String(error);
  job.detail = 'Intelligence failed';
  job.finishedAt = new Date().toISOString();
  emitIntelligenceJobs();
}

function drainIntelligenceQueue(): void {
  while (activeIntelligenceCount < MAX_CONCURRENT_INTELLIGENCE && intelligenceQueue.length > 0) {
    const next = intelligenceQueue.shift()!;
    activeIntelligenceCount++;
    runIntelligencePipeline(next.job, next.item)
      .catch(err => failIntelligenceJob(next.job, err))
      .finally(() => {
        activeIntelligenceCount--;
        drainIntelligenceQueue();
      });
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Queue a KnowledgeItem for the full 21-stage intelligence pipeline.
 * Safe to call multiple times — items already in queue are skipped.
 */
export function enqueueForIntelligence(item: KnowledgeItem): void {
  // Skip if already queued or processed
  if (intelligenceJobs.some(j => j.itemId === item.id && j.status !== 'error')) return;

  const job: IntelligenceJob = {
    id: makeJobId(),
    label: item.title.length > 60 ? `${item.title.slice(0, 57)}…` : item.title,
    itemId: item.id,
    stage: 'rie-capture',
    stageIndex: 0,
    totalStages: INTELLIGENCE_STAGES.length,
    pct: 0,
    detail: 'Queued for intelligence…',
    status: 'running',
    error: null,
    startedAt: new Date().toISOString(),
    finishedAt: null,
  };

  intelligenceJobs.unshift(job);

  // Cap history: drop oldest finished jobs first
  if (intelligenceJobs.length > MAX_INTELLIGENCE_JOBS_KEPT) {
    const finished = intelligenceJobs.filter(j => j.status !== 'running');
    const toDrop = new Set(
      finished.slice(-(intelligenceJobs.length - MAX_INTELLIGENCE_JOBS_KEPT)).map(j => j.id),
    );
    intelligenceJobs = intelligenceJobs.filter(j => !toDrop.has(j.id));
  }

  emitIntelligenceJobs();
  intelligenceQueue.push({ job, item: { ...item } });
  drainIntelligenceQueue();
}

// ─── Intelligence entity dictionaries ────────────────────────────────────────

const KNOWN_COMPANIES = new Set([
  'Upwork','Fiverr','Toptal','Freelancer','Guru','PeoplePerHour','Workana',
  '99designs','DesignCrowd','Dribbble','Behance','GitHub','GitLab','Bitbucket',
  'Stripe','PayPal','Wise','Revolut','Payoneer','Deel','Remote','Rippling',
  'Gusto','Loom','Notion','Slack','Asana','Trello','Jira','Confluence',
  'Figma','Canva','Adobe','Google','Microsoft','Apple','Amazon','Meta',
  'Facebook','Netflix','Spotify','Airbnb','Booking.com','LinkedIn',
  'Twitter','Instagram','TikTok','YouTube','Shopify','HubSpot','Salesforce',
  'Zoom','Dropbox','Box','Airtable','Monday','ClickUp','Workday',
  'Indeed','Glassdoor','AngelList','Crunchbase','Product Hunt','Substack',
  'Medium','Ghost','WordPress','Wix','Squarespace','Webflow',
  'Vercel','Netlify','Heroku','DigitalOcean','Cloudflare','AWS','GCP','Azure',
  'Turing','Andela','Crossover','Arc','Remote OK','We Work Remotely','Nomad List',
  'Transferwise','Payoneer','Brex','Mercury','Wise','N26','Monzo','Revolut',
  'Anthropic','OpenAI','DeepMind','Hugging Face','Stability AI','Midjourney',
]);

const KNOWN_PLACES = new Set([
  // Digital nomad hubs
  'Bali','Chiang Mai','Bangkok','Lisbon','Porto','Barcelona','Madrid','Seville',
  'Berlin','Amsterdam','Prague','Budapest','Tbilisi','Medellin','Bogota',
  'Mexico City','CDMX','Buenos Aires','Playa del Carmen','Tulum','Oaxaca',
  'Cape Town','Nairobi','Lagos','Accra','Dubai','Abu Dhabi','Singapore',
  'Tokyo','Seoul','Ho Chi Minh City','Hanoi','Hoi An','Phnom Penh',
  'Kuala Lumpur','Penang','Manila','Ubud','Canggu','Seminyak',
  'Tbilisi','Batumi','Yerevan','Tashkent','Almaty',
  'Tallinn','Riga','Vilnius','Warsaw','Krakow','Wroclaw',
  'Bucharest','Cluj','Sofia','Athens','Thessaloniki',
  'Tbilisi','Sarajevo','Skopje','Tirana','Podgorica','Novi Sad',
  // Countries
  'Thailand','Indonesia','Portugal','Spain','Germany','Netherlands','France',
  'Italy','Greece','Georgia','Colombia','Mexico','Argentina','Brazil','Chile',
  'Peru','Ecuador','Panama','Costa Rica','Guatemala','Bolivia',
  'South Africa','Kenya','Nigeria','Ghana','Morocco','Egypt',
  'UAE','Qatar','Jordan','Turkey','Israel','Cyprus',
  'Singapore','Japan','South Korea','Vietnam','Philippines','Malaysia',
  'Sri Lanka','Nepal','India','Cambodia','Myanmar','Laos',
  'UK','Ireland','Switzerland','Austria','Belgium','Sweden','Norway','Denmark',
  'Finland','Iceland','Estonia','Latvia','Lithuania','Poland','Czech Republic',
  'Croatia','Slovenia','Montenegro','Albania','Serbia','Romania','Bulgaria',
  'USA','Canada','Australia','New Zealand','Mauritius','Maldives','Seychelles',
  // Regions
  'Europe','Asia','Southeast Asia','Latin America','Africa','Middle East',
  'Central America','Eastern Europe','Western Europe','Scandinavia','Balkans',
  'APAC','LATAM','EMEA','Caribbean','Pacific Islands',
]);

const KNOWN_SKILLS = new Set([
  // Programming
  'JavaScript','TypeScript','Python','Ruby','Go','Rust','Java','C++','C#',
  'Swift','Kotlin','PHP','Scala','R','Perl','Elixir','Clojure','Haskell',
  'React','Vue','Angular','Next.js','Nuxt','Svelte','Node.js','Express',
  'Django','Flask','FastAPI','Rails','Spring','Laravel','Symfony',
  'HTML','CSS','Tailwind','Sass','Bootstrap','Material UI',
  'SQL','PostgreSQL','MySQL','MongoDB','Redis','Elasticsearch','DynamoDB',
  'GraphQL','REST','gRPC','WebSockets','OAuth','JWT',
  'AWS','GCP','Azure','Docker','Kubernetes','Terraform','Ansible','CI/CD',
  'DevOps','SRE','Linux','Bash','Git','GitHub Actions',
  'Machine Learning','AI','LLM','GPT','Prompt Engineering','TensorFlow','PyTorch',
  'Data Science','Pandas','NumPy','Scikit-learn','Tableau','Power BI',
  'Blockchain','Solidity','Web3','Smart Contracts',
  'Cybersecurity','Penetration Testing','Network Security',
  'iOS','Android','React Native','Flutter','Expo',
  'Unity','Unreal Engine','Game Development',
  // Design
  'UI/UX','Figma','Sketch','Adobe XD','Photoshop','Illustrator','InDesign',
  'After Effects','Premiere Pro','Final Cut','DaVinci Resolve','Canva',
  'Motion Design','3D Modeling','Blender','Cinema 4D','Maya','AutoCAD',
  'Photography','Video Editing','Animation','Illustration','Branding',
  // Marketing
  'SEO','SEM','PPC','Google Ads','Meta Ads','Facebook Ads','Email Marketing',
  'Content Marketing','Affiliate Marketing','Influencer Marketing',
  'Social Media','Community Management','Brand Strategy','Growth Hacking',
  'Copywriting','Content Writing','Technical Writing','Ghostwriting',
  'Sales','Business Development','CRM','Lead Generation','Cold Outreach',
  // Business
  'Project Management','Scrum','Agile','Kanban','PMP','Prince2',
  'Product Management','Product Strategy','User Research','A/B Testing',
  'Data Analysis','Excel','Google Sheets','Financial Modeling','Accounting',
  'Bookkeeping','QuickBooks','Xero','Tax','Invoicing',
  'Virtual Assistant','Customer Service','Customer Success','Account Management',
  'Recruiting','HR','Training','Coaching','Consulting','Facilitating',
  'Translation','Transcription','Localization','Proofreading','Editing',
  'E-commerce','Shopify','WooCommerce','Amazon FBA','Dropshipping','Print on Demand',
  'WordPress','Webflow','Wix','Squarespace','Web Development',
  'Research','Academic Writing','Grant Writing','Legal Research',
  'Podcast Production','Storytelling','Public Speaking','Presentation',
]);

const KNOWN_CAREER_TYPES = new Set([
  'Software Developer','Web Developer','Frontend Developer','Backend Developer',
  'Full-Stack Developer','Mobile Developer','iOS Developer','Android Developer',
  'Game Developer','DevOps Engineer','Cloud Engineer','Data Engineer','ML Engineer',
  'Data Analyst','Data Scientist','Machine Learning Engineer','AI Engineer',
  'Graphic Designer','UI Designer','UX Designer','UI/UX Designer','Product Designer',
  'Motion Designer','Video Editor','Animator','3D Artist','Illustrator',
  'Photographer','Videographer','Content Creator','YouTuber','Podcaster',
  'Content Writer','Copywriter','Technical Writer','SEO Specialist',
  'Digital Marketer','Social Media Manager','Email Marketer','PPC Specialist',
  'Virtual Assistant','Executive Assistant','Project Manager','Product Manager',
  'Business Analyst','Consultant','Coach','Trainer','Mentor',
  'Customer Support','Customer Success','Sales Representative','Account Manager',
  'Recruiter','HR Specialist','Accountant','Bookkeeper','Financial Analyst',
  'Translator','Interpreter','Transcriptionist','Proofreader','Editor',
  'Tutor','Teacher','Course Creator','E-learning Developer',
  'E-commerce Manager','Dropshipper','Amazon Seller','Affiliate Marketer',
  'Blockchain Developer','Web3 Developer','Smart Contract Developer',
  'Cybersecurity Analyst','Penetration Tester','Security Consultant',
  'Researcher','Academic','Writer','Journalist','Blogger',
]);

const TOPIC_MAP: Array<{ kws: string[]; topic: string }> = [
  { kws: ['remote work','work from home','wfh','remote job','distributed team'],       topic: 'Remote Work' },
  { kws: ['digital nomad','nomad','location independent','work anywhere','slow travel'], topic: 'Digital Nomad' },
  { kws: ['freelance','freelancing','self-employed','contractor','gig economy'],        topic: 'Freelancing' },
  { kws: ['entrepreneur','startup','founder','bootstrapped','saas','side project'],     topic: 'Entrepreneurship' },
  { kws: ['invest','investing','stock','crypto','portfolio','dividend','passive income'],topic: 'Investing' },
  { kws: ['personal finance','budget','saving','debt','money','wealth building'],        topic: 'Personal Finance' },
  { kws: ['travel','backpack','visa','passport','flights','accommodation'],              topic: 'Travel' },
  { kws: ['productivity','time management','workflow','routine','habits','system'],      topic: 'Productivity' },
  { kws: ['ai','artificial intelligence','machine learning','llm','chatgpt','gpt','prompt'], topic: 'Artificial Intelligence' },
  { kws: ['design','ux','ui','user experience','interface','wireframe','prototype'],     topic: 'Design' },
  { kws: ['marketing','seo','content','email','social media','ads','funnel'],           topic: 'Marketing' },
  { kws: ['sales','revenue','customer','client','pitch','proposal','close'],            topic: 'Sales' },
  { kws: ['leadership','management','team','culture','strategy','vision','hire'],       topic: 'Leadership' },
  { kws: ['health','wellness','fitness','exercise','sleep','nutrition','mental health'], topic: 'Health & Wellness' },
  { kws: ['mindfulness','meditation','breathwork','yoga','presence','awareness'],       topic: 'Mindfulness' },
  { kws: ['language','learn','duolingo','fluent','spanish','french','german'],          topic: 'Language Learning' },
  { kws: ['culture','expat','local','community','coworking','coliving'],                topic: 'Culture & Community' },
  { kws: ['tax','invoice','incorporation','llc','accounting','legal','vat'],            topic: 'Business & Legal' },
  { kws: ['career','job','interview','resume','cv','portfolio','upskill'],              topic: 'Career Development' },
  { kws: ['writing','blog','newsletter','publish','author','content creation'],         topic: 'Writing & Content' },
  { kws: ['code','programming','developer','software','web','app','api','database'],    topic: 'Software Development' },
  { kws: ['crypto','bitcoin','ethereum','web3','nft','blockchain','defi','dao'],        topic: 'Crypto & Web3' },
];

const STOPWORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with',
  'by','from','as','is','was','are','were','be','been','being','have',
  'has','had','do','does','did','will','would','could','should','may',
  'might','shall','can','need','dare','ought','used','this','that','these',
  'those','i','you','he','she','it','we','they','me','him','her','us','them',
  'my','your','his','its','our','their','what','which','who','when','where',
  'how','all','each','every','both','either','neither','such','so','then',
  'than','too','very','just','but','if','because','while','although','though',
  'about','above','after','before','between','into','through','during','also',
  'not','no','nor','yet','up','out','over','under','again','here','there',
]);

// ─── Extraction helpers ───────────────────────────────────────────────────────

function corpus(item: KnowledgeItem): string {
  return [
    item.title,
    item.summary ?? '',
    item.rawContent.slice(0, 8000), // cap to avoid huge text
    ...item.tags,
    ...item.categories,
    item.publisher ?? '',
  ].filter(Boolean).join(' ');
}

function extractCompanies(text: string): string[] {
  const found = new Set<string>();
  const lower = text.toLowerCase();

  for (const co of KNOWN_COMPANIES) {
    if (lower.includes(co.toLowerCase())) found.add(co);
  }

  // Match patterns like "Acme Inc", "Acme Corp", "Acme Ltd", "Acme LLC"
  const corpPattern = /([A-Z][a-zA-Z]+(?: [A-Z][a-zA-Z]+)?)\s+(?:Inc|Corp|Ltd|LLC|GmbH|SAS|BV|AG|SA)\b/g;
  for (const m of text.matchAll(corpPattern)) {
    if (m[1] && m[1].length > 2) found.add(m[1]);
  }

  return Array.from(found).slice(0, 12);
}

function extractPeople(text: string): string[] {
  const found = new Set<string>();

  // Two-word capitalized names (not at start of sentence typically, but anywhere)
  const namePattern = /\b([A-Z][a-z]{1,20}) ([A-Z][a-z]{1,20})\b/g;
  const excludePrefixes = new Set([
    'The', 'This', 'That', 'These', 'Those', 'Such', 'When', 'Where', 'Which',
    'What', 'How', 'From', 'Into', 'Over', 'After', 'Before', 'During',
  ]);

  for (const m of text.matchAll(namePattern)) {
    const first = m[1];
    const last = m[2];
    if (excludePrefixes.has(first) || excludePrefixes.has(last)) continue;
    // Skip known places/companies that match capitalized pattern
    const fullName = `${first} ${last}`;
    if (KNOWN_PLACES.has(first) || KNOWN_PLACES.has(last)) continue;
    if (KNOWN_COMPANIES.has(first) || KNOWN_COMPANIES.has(fullName)) continue;
    found.add(fullName);
  }

  return Array.from(found).slice(0, 8);
}

function extractPlaces(text: string): string[] {
  const found = new Set<string>();
  const lower = text.toLowerCase();

  for (const place of KNOWN_PLACES) {
    if (lower.includes(place.toLowerCase())) found.add(place);
  }

  return Array.from(found).slice(0, 10);
}

function extractSkills(text: string): string[] {
  const found = new Set<string>();
  const lower = text.toLowerCase();

  for (const skill of KNOWN_SKILLS) {
    if (lower.includes(skill.toLowerCase())) found.add(skill);
  }

  return Array.from(found).slice(0, 15);
}

function extractCareerTypes(text: string): string[] {
  const found = new Set<string>();
  const lower = text.toLowerCase();

  for (const career of KNOWN_CAREER_TYPES) {
    if (lower.includes(career.toLowerCase())) found.add(career);
  }

  return Array.from(found).slice(0, 8);
}

function extractTopics(text: string): string[] {
  const found: string[] = [];
  const lower = text.toLowerCase();

  for (const { kws, topic } of TOPIC_MAP) {
    if (kws.some(kw => lower.includes(kw))) {
      found.push(topic);
    }
  }

  return found.slice(0, 8);
}

function extractKeywords(text: string, maxKeywords = 20): string[] {
  // Simple frequency analysis on non-stopword words ≥ 4 characters
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 4 && !STOPWORDS.has(w));

  const freq = new Map<string, number>();
  for (const w of words) {
    freq.set(w, (freq.get(w) ?? 0) + 1);
  }

  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxKeywords)
    .map(([w]) => w);
}

function buildAugmentedSearchIndex(item: KnowledgeItem): string[] {
  const existing = new Set(item.searchIndex);
  const additions = [
    ...item.recognizedCompanies,
    ...item.recognizedPeople,
    ...item.recognizedPlaces,
    ...item.detectedSkills,
    ...item.detectedCareerTypes,
    ...item.topicsAi,
    ...item.keywords.slice(0, 10),
  ];
  for (const term of additions) existing.add(term.toLowerCase());
  return Array.from(existing).slice(0, 100);
}

// ─── Small async delay (prevents blocking the UI thread) ─────────────────────

const tick = (ms = 30) => new Promise<void>(r => setTimeout(r, ms));

// ─── 21-stage pipeline ────────────────────────────────────────────────────────

async function runIntelligencePipeline(
  job: IntelligenceJob,
  item: KnowledgeItem,
): Promise<void> {
  const txt = corpus(item);

  // Stage 0: Capture — validate item has content
  advanceJob(job, 0, 'Validating capture…');
  await tick();
  if (!item.id || !item.title) throw new Error('Item is incomplete — cannot process.');

  // Stage 1: Identify Content Type
  advanceJob(job, 1, `Content type: ${item.contentType}`);
  await tick();

  // Stage 2: Extract Metadata
  advanceJob(job, 2, `Source: ${item.originalSource ? 'URL' : 'local'}`);
  await tick();

  // Stage 3: Extract Text
  advanceJob(job, 3, `${item.wordCount.toLocaleString()} words extracted`);
  await tick();

  // Stage 4: OCR (skip if not photo)
  advanceJob(job, 4, ['photo'].includes(item.contentType) ? 'Running OCR…' : 'Skipped (not an image)');
  await tick();

  // Stage 5: Transcript (skip if not audio/video)
  advanceJob(job, 5, ['voice','audio','youtube','podcast'].includes(item.contentType)
    ? 'Checking transcript…' : 'Skipped (not audio/video)');
  await tick();

  // Stage 6: AI Summary (already done by capture pipeline — verify)
  advanceJob(job, 6, item.summary ? 'Summary verified ✓' : 'No summary (short content)');
  await tick();

  // Stage 7: Extract Topics
  advanceJob(job, 7, 'Mapping topic categories…');
  await tick();
  item.topicsAi = extractTopics(txt);
  await tick(20);

  // Stage 8: Extract Keywords
  advanceJob(job, 8, 'Running keyword frequency analysis…');
  await tick();
  item.keywords = extractKeywords(txt);
  await tick(30);

  // Stage 9: Recognize Companies
  advanceJob(job, 9, 'Scanning for company mentions…');
  await tick();
  item.recognizedCompanies = extractCompanies(txt);
  await tick(30);

  // Stage 10: Recognize People
  advanceJob(job, 10, 'Scanning for person mentions…');
  await tick();
  item.recognizedPeople = extractPeople(txt);
  await tick(20);

  // Stage 11: Recognize Places
  advanceJob(job, 11, 'Scanning for location mentions…');
  await tick();
  item.recognizedPlaces = extractPlaces(txt);
  await tick(20);

  // Stage 12: Recognize Skills
  advanceJob(job, 12, 'Mapping detected skills…');
  await tick();
  item.detectedSkills = extractSkills(txt);
  await tick(30);

  // Stage 13: Recognize Career Types
  advanceJob(job, 13, 'Matching career types…');
  await tick();
  item.detectedCareerTypes = extractCareerTypes(txt);
  await tick(20);

  // Stage 14: Suggest Collections (already done by capture pipeline — verify)
  advanceJob(job, 14, item.collections.length > 0
    ? `${item.collections.length} collection(s) assigned` : 'No collections suggested');
  await tick();

  // Stage 15: Generate Related Content (future — placeholder)
  advanceJob(job, 15, 'Building topic connections…');
  await tick();
  item.relatedContent = item.topicsAi.map(t => `More about ${t}`).slice(0, 3);
  await tick(20);

  // Stage 16: Create Search Index
  advanceJob(job, 16, 'Augmenting search index…');
  await tick();
  const augmented = buildAugmentedSearchIndex(item);
  item.searchIndex = augmented;
  await tick(20);

  // Stage 17: Update Knowledge Graph (stub — logs only in this sprint)
  advanceJob(job, 17, 'Updating knowledge connections…');
  await tick(50);
  // Future: import { updateKnowledgeGraph } from '../vault/VaultKnowledgeGraph';

  // Stage 18: Store Original (verify)
  advanceJob(job, 18, `Storage: ${item.storageLocation}`);
  await tick();

  // Stage 19: Store Intelligence — write enriched item to IndexedDB
  advanceJob(job, 19, 'Saving intelligence layer…');
  item.intelligenceStatus = 'complete';
  item.intelligenceStage = 'rie-store-intel';
  item.intelligenceProcessedAt = new Date().toISOString();

  try {
    await updateKnowledgeItem(item.id, {
      recognizedCompanies:   item.recognizedCompanies,
      recognizedPeople:      item.recognizedPeople,
      recognizedPlaces:      item.recognizedPlaces,
      detectedSkills:        item.detectedSkills,
      detectedCareerTypes:   item.detectedCareerTypes,
      topicsAi:              item.topicsAi,
      keywords:              item.keywords,
      relatedContent:        item.relatedContent,
      searchIndex:           item.searchIndex,
      intelligenceStatus:    'complete',
      intelligenceStage:     'rie-store-intel',
      intelligenceProcessedAt: item.intelligenceProcessedAt,
    });
  } catch {
    // Non-fatal — intelligence may not persist to IndexedDB in all environments
  }

  // Stage 20: Complete
  advanceJob(job, 20, 'Intelligence complete ✓');
  await tick();

  finishIntelligenceJob(job);

  // Emit KNOWLEDGE_EVENTS.updated so UI refreshes item details
  window.dispatchEvent(new CustomEvent('nomad:knowledge-updated'));
}
